/**
 * Cliente de tempo real (WebSocket) — avisa quando algo muda no back-end
 * (criacao/edicao/movimentacao etc.) pra quem esta com a tela aberta atualizar
 * sozinho, sem precisar de F5. Uma unica conexao por sessao do navegador,
 * compartilhada entre todas as paginas que assinarem via `subscribeRealtime`.
 */

type Handler = () => void;

const bus = new EventTarget();
let socket: WebSocket | null = null;
let conectando = false;
let tentativas = 0;

function urlRealtime(): string {
  const protocolo = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocolo}//${window.location.host}/api/realtime`;
}

function agendarReconexao() {
  tentativas += 1;
  const espera = Math.min(15000, 2000 * tentativas);
  window.setTimeout(conectar, espera);
}

function conectar() {
  if (conectando || (socket && socket.readyState === WebSocket.OPEN)) return;
  conectando = true;
  const ws = new WebSocket(urlRealtime());
  socket = ws;

  ws.onopen = () => {
    conectando = false;
    tentativas = 0;
  };

  ws.onmessage = (evento) => {
    try {
      const dados = JSON.parse(evento.data);
      if (dados && typeof dados.rpc === "string") {
        bus.dispatchEvent(new CustomEvent("mudanca", { detail: { rpc: dados.rpc } }));
      }
    } catch {
      /* mensagem invalida: ignora */
    }
  };

  ws.onclose = () => {
    conectando = false;
    socket = null;
    agendarReconexao();
  };

  ws.onerror = () => {
    ws.close();
  };
}

/**
 * Assina mudanças cujo nome de RPC contenha algum dos `padroes` (comparação
 * sem diferenciar maiúsculas/minúsculas). `onChange` roda com um pequeno
 * debounce para não disparar várias vezes quando várias mudanças chegam
 * juntas. Retorna a função de cancelamento da assinatura.
 */
export function subscribeRealtime(padroes: string[], onChange: Handler): () => void {
  conectar();
  const padroesMin = padroes.map((padrao) => padrao.toLowerCase());
  let timer: number | null = null;

  const ouvinte = (evento: Event) => {
    const rpc = String((evento as CustomEvent<{ rpc: string }>).detail?.rpc || "").toLowerCase();
    if (!padroesMin.some((padrao) => rpc.includes(padrao))) return;
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = null;
      onChange();
    }, 400);
  };

  bus.addEventListener("mudanca", ouvinte);
  return () => {
    bus.removeEventListener("mudanca", ouvinte);
    if (timer) window.clearTimeout(timer);
  };
}
